import { useCallback, useEffect, useMemo, useState } from 'react'

import { pocketbaseErrorMessage } from '../../lib/pocketbaseErrorMessage'
import {
  COL_MACHINES,
  COL_SCHEDULE_SLOTS,
  COL_TIMESHEET_ENTRIES,
} from '../../config/collections'
import { groupMachinesByType, machineTypeAccent, machineTypeLabel } from '../../lib/machines'
import { fetchCalendarMap } from '../../lib/loadProductionCalendar'
import { resolveWorkingBrigade } from '../../lib/shiftRotation'
import { ShiftFilterSelect } from '../../components/ShiftFilterSelect'
import { useAssignedShiftFilter } from '../../hooks/useAssignedShiftFilter'
import { pb } from '../../pocketbase'
import {
  formatDeviation,
  formatQty,
  SCHEDULE_SLOT_STATUS_LABELS,
} from '../../lib/scheduleCalculations'
import type { MachineRecord, ScheduleSlotRecord, TimesheetEntryRecord } from '../../types/records'
import { Alert } from '../../components/ui'
import { cardClass, cn, loadingClass } from '../../lib/ui'

const REFRESH_MS = 30_000

function dateInputValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

export function KioskPage() {
  const { shiftId, setShiftId, shifts, canAllShifts, shiftLocked, appendShiftFilter } =
    useAssignedShiftFilter()
  const [machines, setMachines] = useState<MachineRecord[]>([])
  const [slots, setSlots] = useState<ScheduleSlotRecord[]>([])
  const [timesheets, setTimesheets] = useState<TimesheetEntryRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')
  const [lastUpdate, setLastUpdate] = useState<Date>(() => new Date())

  const today = dateInputValue(new Date())

  useEffect(() => {
    let cancelled = false
    void fetchCalendarMap(today, today).then((map) => {
      if (cancelled) return
      const brigade = resolveWorkingBrigade(today, map)
      if (brigade && (canAllShifts || !shiftLocked)) {
        setShiftId(brigade)
      }
    })
    return () => {
      cancelled = true
    }
  }, [today, canAllShifts, shiftLocked, setShiftId])

  const load = useCallback(async () => {
    setErr('')
    try {
      const dayStart = `${today} 00:00:00`
      const dayEnd = `${today} 23:59:59`

      const slotFilter = appendShiftFilter(
        `plannedStart <= "${dayEnd}" && plannedEnd >= "${dayStart}" && status != "cancelled"`,
      )
      let tsFilter = `workDate >= "${dayStart}" && workDate <= "${dayEnd}" && present = true`
      tsFilter = appendShiftFilter(tsFilter)

      const [mList, slotList, tsList] = await Promise.all([
        pb.collection(COL_MACHINES).getFullList<MachineRecord>({ sort: 'type,sort,number' }),
        pb.collection(COL_SCHEDULE_SLOTS).getFullList<ScheduleSlotRecord>({
          filter: slotFilter,
          expand: 'requestLine.product,machine,shift',
          sort: 'plannedStart',
        }),
        pb.collection(COL_TIMESHEET_ENTRIES).getFullList<TimesheetEntryRecord>({
          filter: tsFilter,
          expand: 'worker,machine,shift',
        }),
      ])

      setMachines(mList)
      setSlots(slotList)
      setTimesheets(tsList)
      setLastUpdate(new Date())
    } catch (e: unknown) {
      setErr(pocketbaseErrorMessage(e, 'Ошибка загрузки'))
    } finally {
      setLoading(false)
    }
  }, [today, appendShiftFilter])

  useEffect(() => {
    void load()
    const id = window.setInterval(() => void load(), REFRESH_MS)
    return () => window.clearInterval(id)
  }, [load])

  const machineGroups = useMemo(() => groupMachinesByType(machines), [machines])

  function currentSlotForMachine(machineId: string): ScheduleSlotRecord | null {
    const now = Date.now()
    const active = slots.filter((s) => {
      if (s.machine !== machineId) return false
      const start = new Date(s.plannedStart).getTime()
      const end = new Date(s.plannedEnd).getTime()
      return start <= now && now <= end && s.status !== 'cancelled'
    })
    active.sort((a, b) => {
      if (a.status === 'in_progress' && b.status !== 'in_progress') return -1
      if (b.status === 'in_progress' && a.status !== 'in_progress') return 1
      return new Date(a.plannedStart).getTime() - new Date(b.plannedStart).getTime()
    })
    return active[0] ?? slots.find((s) => s.machine === machineId && s.status === 'in_progress') ?? null
  }

  function workersOnMachine(machineId: string): string[] {
    return timesheets
      .filter((t) => t.machine === machineId && t.present)
      .map((t) => t.expand?.worker?.name ?? '—')
      .filter(Boolean)
  }

  if (loading && machines.length === 0) {
    return (
      <div className={cn(loadingClass, 'min-h-[60vh] text-lg')}>
        Загрузка экрана смены…
      </div>
    )
  }

  return (
    <div className="kiosk-screen -mx-4 -mt-2 min-h-[calc(100vh-6rem)] bg-slate-900 p-4 text-white md:-mx-6 md:p-6">
      <header className="mb-6 flex flex-wrap items-center justify-between gap-4 border-b border-white/15 pb-4">
        <div>
          <h2 className="m-0 text-2xl font-bold tracking-tight md:text-3xl">Экран смены</h2>
          <p className="mt-1 text-sm text-slate-300">
            {new Date().toLocaleString('ru-RU', { dateStyle: 'full', timeStyle: 'short' })}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <ShiftFilterSelect
            shiftId={shiftId}
            shifts={shifts}
            canAllShifts={canAllShifts}
            locked={shiftLocked}
            onChange={setShiftId}
            className="text-slate-200 [&_select]:bg-slate-800 [&_select]:text-white"
          />
          <div className="text-right text-sm text-slate-400">
            Обновлено: {lastUpdate.toLocaleTimeString('ru-RU')}
            <br />
            авто каждые {REFRESH_MS / 1000} с
          </div>
        </div>
      </header>

      {err && (
        <div className="mb-4">
          <Alert kind="err">{err}</Alert>
        </div>
      )}

      {shifts.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-2 text-sm">
          {shifts.map((s) => (
            <span key={s.id} className="rounded-full bg-white/10 px-3 py-1">
              {s.name}: {s.startTime}–{s.endTime}
            </span>
          ))}
        </div>
      )}

      {machineGroups.map((group) => (
        <section key={group.type} className="mb-8">
          <h3 className="mb-3 text-lg font-semibold" style={{ color: group.accent }}>
            {group.label}
          </h3>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {group.items.map((machine) => {
              const slot = currentSlotForMachine(machine.id)
              const crew = workersOnMachine(machine.id)
              const planned = slot ? Number(slot.plannedQuantity) || 0 : 0
              const actual = slot?.actualQuantity != null ? Number(slot.actualQuantity) : null

              return (
                <article
                  key={machine.id}
                  className={cn(cardClass, 'border-white/10 bg-slate-800/90 p-5 text-white shadow-lg')}
                >
                  <div className="mb-3 flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span
                        className="inline-block h-3 w-3 rounded-full"
                        style={{ backgroundColor: machineTypeAccent(machine.type) }}
                      />
                      <h4 className="m-0 text-xl font-bold">
                        {machineTypeLabel(machine.type)} №{machine.number}
                      </h4>
                    </div>
                    {slot && (
                      <span
                        className={cn(
                          'rounded-full px-2 py-0.5 text-xs font-semibold',
                          slot.status === 'in_progress'
                            ? 'bg-emerald-500/25 text-emerald-200'
                            : 'bg-indigo-500/25 text-indigo-200',
                        )}
                      >
                        {SCHEDULE_SLOT_STATUS_LABELS[slot.status]}
                      </span>
                    )}
                  </div>

                  {slot ? (
                    <>
                      <p className="mb-1 text-lg font-medium text-slate-100">
                        {slot.expand?.requestLine?.expand?.product?.name ?? 'Задание'}
                      </p>
                      <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                        <div className="rounded-lg bg-white/5 p-2">
                          <p className="text-xs text-slate-400">План</p>
                          <p className="text-2xl font-bold">{formatQty(planned)}</p>
                        </div>
                        <div className="rounded-lg bg-white/5 p-2">
                          <p className="text-xs text-slate-400">Факт</p>
                          <p className="text-2xl font-bold">{actual != null ? formatQty(actual) : '—'}</p>
                        </div>
                        <div className="rounded-lg bg-white/5 p-2">
                          <p className="text-xs text-slate-400">Δ</p>
                          <p className="text-2xl font-bold">{formatDeviation(planned, actual)}</p>
                        </div>
                      </div>
                    </>
                  ) : (
                    <p className="text-slate-400">Нет активного задания</p>
                  )}

                  <div className="mt-4 border-t border-white/10 pt-3">
                    <p className="text-xs uppercase tracking-wide text-slate-500">На станке</p>
                    <p className="mt-1 text-base font-medium">
                      {crew.length > 0 ? crew.join(', ') : '—'}
                    </p>
                  </div>
                </article>
              )
            })}
          </div>
        </section>
      ))}

      {machines.length === 0 && (
        <p className="text-center text-slate-400">
          <em>Нет станков для отображения</em>
        </p>
      )}
    </div>
  )
}
